import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn';
import { Spinner } from './Spinner';

// Заблокированные кнопки по макетам РТК — серые, а не выцветшие оранжевые,
// поэтому disabled-цвета задаются повариантно, без общего opacity.
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-control)] font-medium transition-colors disabled:pointer-events-none select-none',
  {
    variants: {
      variant: {
        primary: 'bg-brand text-white hover:bg-brand-hover disabled:bg-border disabled:text-fg-muted',
        secondary:
          'bg-brand-soft text-brand hover:bg-brand-softhover disabled:bg-border disabled:text-fg-muted',
        outline:
          'border border-border-strong bg-surface text-fg hover:bg-page disabled:border-border disabled:bg-page disabled:text-fg-muted',
        ghost: 'text-fg hover:bg-page disabled:text-fg-muted',
        danger: 'bg-danger text-white hover:opacity-90 disabled:bg-border disabled:text-fg-muted',
        link: 'text-brand underline-offset-2 hover:underline p-0 h-auto disabled:text-fg-muted disabled:no-underline',
      },
      size: {
        sm: 'h-9 px-3 text-sm',
        md: 'h-11 px-5 text-sm',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, loading, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled ?? loading}
        {...props}
      >
        {loading ? (
          <>
            <Spinner className="h-4 w-4" />
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';
